/* Mes devoirs — teacher assignments list (roadmap-v1 bloc 02, écran B). */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from '../lib/routing';
import Header from '../components/header';
import { HeaderContent, HEADER_TITLE_CLASS } from '../components/shell/app-header';
import { api } from '../lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import {
  Clock, Warning, Tray, Users, CaretRight,
  WarningCircle, ArrowClockwise, FolderOpen,
} from '@phosphor-icons/react';
import { groupAssignmentsByClassroomAndSubject } from '@naia-app/shared';
import type { AssignmentSummary } from '../data/teacher-dummy';
import { assignmentFormatLabel, AssignmentFormatIcon } from '../lib/assignment-format';

function dueGroupTimestamp(a: AssignmentSummary): number {
  return a.due_at ? new Date(a.due_at).getTime() : Number.POSITIVE_INFINITY;
}

/* Groupe classe -> matière (si la classe en compte plusieurs) -> due_at
   croissant (consigne Ismaël). Les noms de classe/matière viennent des devoirs
   eux-mêmes (chaque ligne les porte) : pas d'appel réseau supplémentaire. */
function useGroupedAssignments(items: AssignmentSummary[]) {
  return useMemo(() => {
    const classroomNames = new Map<string, string>();
    const subjectLabels = new Map<string, string>();
    const subjectPositions = new Map<string, number>();
    for (const a of items) {
      for (const c of a.classrooms) classroomNames.set(c.id, c.name);
      subjectLabels.set(a.subject_id, a.subject_label);
      subjectPositions.set(a.subject_id, a.subject_position);
    }

    const groups = groupAssignmentsByClassroomAndSubject(items, {
      classroomIds: (a) => a.classroom_ids,
      subjectId: (a) => a.subject_id,
      subjectSortKey: (subjectId) => subjectPositions.get(subjectId) ?? Number.POSITIVE_INFINITY,
      dueTimestamp: dueGroupTimestamp,
    });

    const sorted = [...groups].sort((a, b) =>
      (classroomNames.get(a.classroomId) ?? '').localeCompare(classroomNames.get(b.classroomId) ?? '', 'fr'),
    );

    return { groups: sorted, classroomNames, subjectLabels };
  }, [items]);
}

function DueLabel({ due_at }: { due_at: string }) {
  const diffDays = (new Date(due_at).getTime() - Date.now()) / 86400000;
  const label = new Date(due_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });

  if (diffDays < 0) {
    return (
      <span className="inline-flex items-center gap-1 text-destructive font-semibold">
        <Warning size={12} weight="fill" />
        En retard · {label}
      </span>
    );
  }
  if (diffDays <= 3) {
    return (
      <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400 font-semibold">
        <Clock size={12} weight="fill" />
        Urgent · {label}
      </span>
    );
  }
  return <span className="text-muted-foreground">À rendre le {label}</span>;
}

function AssignmentRow({ a, onClick }: { a: AssignmentSummary; onClick: () => void }) {
  const isSent = a.status === 'sent';
  const isOverdue = a.due_at && (new Date(a.due_at).getTime() - Date.now()) < 0;
  const isUrgent = a.due_at && !isOverdue && (new Date(a.due_at).getTime() - Date.now()) / 86400000 <= 3;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "group flex items-center gap-3 w-full px-4 py-3 text-left hover:bg-muted/50 transition-colors font-[inherit] bg-transparent border-none cursor-pointer",
        isOverdue && "bg-destructive/[0.03] hover:bg-destructive/[0.06]",
        isUrgent && "bg-amber-50/60 hover:bg-amber-50 dark:bg-amber-950/20 dark:hover:bg-amber-950/30",
      )}
    >
      <div className={cn(
        "shrink-0 w-1.5 self-stretch rounded-full",
        isSent && !isOverdue && !isUrgent && "bg-emerald-400",
        isOverdue && "bg-destructive",
        isUrgent && "bg-amber-400",
        !isSent && "bg-border",
      )} />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="font-semibold text-[14px] text-foreground truncate">{a.title}</span>
          <Badge variant="outline" className="shrink-0 hidden sm:inline-flex gap-1 text-muted-foreground">
            <AssignmentFormatIcon format={a.format} />
            {assignmentFormatLabel(a.format)}
          </Badge>
        </div>
        <div className="flex items-center gap-3 text-[12px] flex-wrap">
          <span className="inline-flex items-center gap-1 text-muted-foreground">
            <Users size={11} />
            {a.classroom_ids.length} classe{a.classroom_ids.length > 1 ? 's' : ''} · {a.student_count} élève{a.student_count > 1 ? 's' : ''}
          </span>
          {a.due_at && <DueLabel due_at={a.due_at} />}
          {!a.due_at && a.published_at && (
            <span className="text-muted-foreground">
              Envoyé le {new Date(a.published_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
            </span>
          )}
          {!a.due_at && !a.published_at && (
            <span className="text-muted-foreground">
              Créé le {new Date(a.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
            </span>
          )}
        </div>
      </div>

      <CaretRight size={14} className="shrink-0 text-muted-foreground opacity-0 pointer-coarse:opacity-100 group-hover:opacity-100 transition-opacity" />
    </button>
  );
}

function AssignmentList({ items, onClick }: { items: readonly AssignmentSummary[]; onClick: (id: string) => void }) {
  if (!items.length) return null;
  return (
    <div className="flex flex-col rounded-card border border-border overflow-hidden shadow-card">
      {items.map((a, i) => (
        <div key={a.id}>
          {i > 0 && <Separator />}
          <AssignmentRow a={a} onClick={() => onClick(a.id)} />
        </div>
      ))}
    </div>
  );
}

function GroupedAssignments({ items, onClick }: { items: AssignmentSummary[]; onClick: (id: string) => void }) {
  const { groups, classroomNames, subjectLabels } = useGroupedAssignments(items);
  if (!items.length) return null;

  return (
    <div className="flex flex-col gap-6">
      {groups.map((g) => (
        <div key={g.classroomId}>
          <h2 className="text-[13px] font-semibold text-foreground/80 mb-2 px-0.5">
            {classroomNames.get(g.classroomId) ?? 'Classe'}
          </h2>
          <div className="flex flex-col gap-4">
            {g.subjectGroups.map((sg) => (
              <div key={sg.subjectId}>
                {g.subjectGroups.length > 1 && (
                  <h3 className="text-[12px] font-medium text-muted-foreground mb-1.5 px-0.5">
                    {subjectLabels.get(sg.subjectId) ?? ''}
                  </h3>
                )}
                <AssignmentList items={sg.items} onClick={onClick} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ label, hint, action }: { label: string; hint?: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center py-14 text-muted-foreground text-center px-4">
      <Tray size={36} weight="light" className="mb-3 opacity-40" />
      <p className="font-semibold text-[14px] m-0">{label}</p>
      {hint && <p className="text-[13px] mt-1.5 mb-0 max-w-[34ch]">{hint}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

function LoadFailed({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-14 text-muted-foreground text-center px-4">
      <WarningCircle size={36} weight="light" className="mb-3 text-destructive opacity-80" />
      <p className="font-semibold text-[14px] text-foreground m-0">Vos devoirs n'ont pas pu être chargés</p>
      <p className="text-[13px] mt-1.5 mb-0 max-w-[34ch]">
        Cet écran n'a pas pu joindre le serveur. Vos devoirs sont intacts.
      </p>
      <Button onClick={onRetry} className="mt-4">
        <ArrowClockwise size={16} weight="bold" />
        Réessayer
      </Button>
    </div>
  );
}

export default function Assignments() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<AssignmentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setFailed(false);
    api.assignments.list
      .query()
      .then((d) => setRows(d as AssignmentSummary[]))
      .catch(() => {
        setRows([]);
        setFailed(true);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const sent = rows.filter((a) => a.status === 'sent');
  const drafts = rows.filter((a) => a.status !== 'sent');
  const go = (id: string) => void navigate(`/teacher/assignments/${id}`);

  return (
    <div className="min-h-dvh bg-background flex flex-col">
      <Header />
      <HeaderContent width="md" showBurger>
        <h1 className={HEADER_TITLE_CLASS}>Mes devoirs</h1>
      </HeaderContent>
      <div className="max-w-[880px] mx-auto px-4 pt-4 pb-16 flex-1 w-full">
        {loading ? (
          <div className="flex flex-col gap-2">
            {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-[58px] rounded-card" />)}
          </div>
        ) : failed ? (
          <LoadFailed onRetry={load} />
        ) : rows.length === 0 ? (
          <EmptyState
            label="Aucun devoir pour l'instant"
            hint="Les devoirs partent d'un document de votre bibliothèque."
            action={(
              <Button render={<Link to="/teacher/library" />}>
                <FolderOpen size={16} weight="fill" />
                Aller à ma bibliothèque
              </Button>
            )}
          />
        ) : (
          <Tabs defaultValue="sent">
            <TabsList className="mb-4 pointer-coarse:h-auto!">
              <TabsTrigger value="sent" className="text-foreground/80 pointer-coarse:h-11!">
                Envoyés
                {sent.length > 0 && (
                  <span className="ml-1.5 text-[11px] font-bold bg-primary/10 text-foreground rounded-full px-1.5 py-0.5 tabular-nums">
                    {sent.length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="drafts" className="text-foreground/80 pointer-coarse:h-11!">
                Brouillons
                {drafts.length > 0 && (
                  <span className="ml-1.5 text-[11px] font-bold bg-muted text-muted-foreground rounded-full px-1.5 py-0.5 tabular-nums">
                    {drafts.length}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="sent">
              {sent.length === 0
                ? <EmptyState label="Aucun devoir envoyé" />
                : <GroupedAssignments items={sent} onClick={go} />}
            </TabsContent>

            <TabsContent value="drafts">
              {drafts.length === 0
                ? <EmptyState label="Aucun brouillon" />
                : <GroupedAssignments items={drafts} onClick={go} />}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}
